// Generic tracking-item table shell. Handles loading/error/empty/rows states
// uniformly across all tracking tabs, plus deep-link affordances
// (highlight ring + smooth scrollIntoView) and inline expand slots for
// per-row detail views. Rows are semantic <button>s wrapped in table cells
// to preserve focus order while keeping proper table semantics.

import { Fragment, useEffect, useRef, type ReactNode } from "react";
import { clsx } from "clsx";
import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";
import LoadingSpinner from "../../../common/LoadingSpinner";
import Alert from "../../../common/Alert";

/** Sort metadata supplied by the caller for a sortable column. The
 *  caller owns the sort state — this component just renders the
 *  click-to-cycle button + caret + aria-sort attribute. */
export interface ColumnSort {
  ariaSort: "ascending" | "descending" | "none";
  onSortClick: () => void;
}

export interface Column<T> {
  header: string;
  cell: (item: T) => ReactNode;
  width?: string;
  align?: "left" | "right";
  /** Extra classes applied to the `<td>` for this column — used for things
   *  like `tabular-nums` on numeric columns where the alignment lives on
   *  the cell, not the cell content. */
  cellClassName?: string;
  /** When true and onRowClick is supplied, the cell content is wrapped in
   *  a focusable button that fires onRowClick. Index 0 is wrapped
   *  unconditionally for backwards compatibility; additional columns
   *  (e.g. the milestone Name column) opt in via this flag so the row's
   *  primary identifier is also click-to-expand. */
  clickable?: boolean;
  /** When supplied, the header renders a sortable <button> + caret per
   *  brand SKILL.md §9.3 and reports `aria-sort` on the <th>. */
  sort?: ColumnSort;
}

export interface TrackingTableProps<T> {
  columns: Column<T>[];
  items: T[];
  isLoading: boolean;
  error: unknown;
  emptyMessage: string;
  rowKey: (item: T) => string;
  onRowClick?: (item: T) => void;
  density?: "standard" | "compact";
  highlightRowId?: string | null;
  expandedRowId?: string | null;
  renderExpandedRow?: (item: T) => ReactNode;
  /** Optional per-row className. Used by InsuranceTab to demote historical
   *  (renewed-but-expired) rows with reduced opacity + slate text so the
   *  audit-only row recedes against active records. */
  rowClassName?: (item: T) => string;
  /** Table layout algorithm. `auto` (default) lets browsers size columns to
   *  content; `fixed` honours per-column `width` hints, which is required
   *  when the same column definitions render across stacked tables (e.g.
   *  the grouped milestone view) so column geometry stays consistent. */
  tableLayout?: "auto" | "fixed";
}

export default function TrackingTable<T>({
  columns,
  items,
  isLoading,
  error,
  emptyMessage,
  rowKey,
  onRowClick,
  density = "standard",
  highlightRowId,
  expandedRowId,
  renderExpandedRow,
  rowClassName,
  tableLayout = "auto",
}: TrackingTableProps<T>) {
  const highlightRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    if (highlightRowId && highlightRef.current) {
      highlightRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [highlightRowId]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error">
        {error instanceof Error ? error.message : "Failed to load tracking items."}
      </Alert>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white px-4 py-12 text-center dark:border-slate-700 dark:bg-slate-800">
        <p className="text-body text-slate-500 dark:text-slate-400">
          {emptyMessage}
        </p>
      </div>
    );
  }

  const cellPadding = density === "compact" ? "px-3 py-2" : "px-4 py-3";

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
      <table
        className={clsx("w-full", tableLayout === "fixed" && "table-fixed")}
      >
        <thead>
          <tr className="bg-slate-50 dark:bg-slate-800">
            {columns.map((col, i) => {
              const headerClasses = clsx(
                cellPadding,
                "text-overline uppercase text-slate-600 dark:text-slate-400",
                col.align === "right" ? "text-right" : "text-left",
                col.width,
              );
              if (col.sort) {
                const { ariaSort, onSortClick } = col.sort;
                const isActive = ariaSort !== "none";
                // Pin the th's accessible name to the column label so callers
                // can target the columnheader by its visible text. The button
                // inside still announces "Sort by …" to screen readers via
                // its own accessible name (independent of the th's).
                return (
                  <th
                    key={i}
                    aria-sort={ariaSort}
                    aria-label={col.header}
                    className={headerClasses}
                  >
                    <button
                      type="button"
                      onClick={onSortClick}
                      className={clsx(
                        "group inline-flex items-center gap-1 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 rounded-sm",
                        col.align === "right" ? "justify-end" : "justify-start",
                      )}
                    >
                      <span className="sr-only">Sort by </span>
                      <span className="uppercase">{col.header}</span>
                      <span
                        className={
                          isActive
                            ? "text-brand-600 dark:text-brand-400"
                            : "opacity-25 group-hover:opacity-60"
                        }
                        aria-hidden="true"
                      >
                        {ariaSort === "ascending" ? (
                          <ChevronUp className="h-3.5 w-3.5" />
                        ) : ariaSort === "descending" ? (
                          <ChevronDown className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronsUpDown className="h-3.5 w-3.5" />
                        )}
                      </span>
                    </button>
                  </th>
                );
              }
              return (
                <th key={i} className={headerClasses}>
                  {col.header}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="[&>tr:not(:first-child)]:border-t [&>tr:not(:first-child)]:border-t-slate-200 dark:[&>tr:not(:first-child)]:border-t-slate-700">
          {items.map((item) => {
            const key = rowKey(item);
            const isHighlighted = key === highlightRowId;
            const isExpanded = key === expandedRowId;
            const isClickable = !!onRowClick;
            return (
              <Fragment key={key}>
                <tr
                  data-testid={`tracking-row-${key}`}
                  ref={isHighlighted ? highlightRef : null}
                  className={clsx(
                    "hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors",
                    isHighlighted &&
                      "ring-2 ring-brand-100 dark:ring-brand-900 transition-all duration-300",
                    // Selected (expanded) parent row: a connected brand
                    // left-border + a quiet neutral tint so the source of
                    // the panel below is obvious. Emphasis comes from the
                    // border, not a heavy brand fill.
                    isExpanded &&
                      "border-l-2 border-brand-500 bg-slate-100 dark:bg-slate-700",
                    rowClassName?.(item),
                  )}
                >
                  {columns.map((col, ci) => {
                    const wrapWithButton =
                      isClickable && (ci === 0 || col.clickable === true);
                    return (
                      <td
                        key={ci}
                        role="cell"
                        className={clsx(
                          cellPadding,
                          "text-body",
                          col.align === "right" ? "text-right" : "text-left",
                          col.cellClassName,
                        )}
                      >
                        {wrapWithButton ? (
                          <button
                            type="button"
                            onClick={() => onRowClick!(item)}
                            className="w-full text-left focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 rounded-sm"
                          >
                            {col.cell(item)}
                          </button>
                        ) : (
                          col.cell(item)
                        )}
                      </td>
                    );
                  })}
                </tr>
                {isExpanded && renderExpandedRow && (
                  <tr data-testid={`tracking-row-${key}-expanded`}>
                    {/* Quiet neutral band with a thin brand connector to the
                     *  selected parent row above. The white detail card
                     *  surface (supplied by TrackingDetailPanel) sits inside
                     *  this band to give the box-in-box read — the band no
                     *  longer carries a broad brand fill. */}
                    <td
                      role="cell"
                      colSpan={columns.length}
                      className="bg-slate-50 dark:bg-slate-800/40 border-l-2 border-brand-500 rounded-r-md p-3"
                    >
                      {renderExpandedRow(item)}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
